import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FAQNavLeft from '../UI/FAQNavLeft/FAQNavLeft';
import styles from './FAQCategories.module.scss';
import FAQCategoriesDropdownMenu from './FAQCategoriesDropdownMenu';
import FAQSearchBar from './FAQSearchBar';
import useFetchFAQs from '../../hooks/useApi/useFetchFAQs';
import useFetchFAQCategories from '../../hooks/useApi/useFetchFAQCategories';
import useDataFetchIndicator from '../../hooks/useDataFetchIndicator';

const { SubTitle } = vp;

type FAQCategoriesProps = {
  categoryName: string;
};

type FAQCategoriesCards = {
  title: string;
  position: string;
  info: string[];
  logo: string;
};

type FC = FunctionComponent<FAQCategoriesProps>;

const FAQCategories: FC = () => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const [faqs, fetchFAQs] = useFetchFAQs({});
  const [faqCategories, setFaqCategories] = useState<vp.FAQCategoryList[]>();
  const [faqCategory, setFaqCategory] = useState<vp.FAQCategoryList>();
  const [faqSubCategories, setFaqSubCategories] = useState<{ [key: string]: vp.FAQSubCategoryList }>({});
  const [categories, fetchFAQCategories] = useFetchFAQCategories({});
  const [faqCategoriesList, setFaqCategoriesList] = useState<FAQCategoriesCards[]>([]);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    fetchFAQs();
    fetchFAQCategories();
  }, []);

  useEffect(() => {
    if (faqs.Success) {
      const faqList = Object.values(faqs.FAQCategories);
      setFaqCategories(faqList);
    }
  }, [faqs]);

  useEffect(() => {
    if (faqCategories) {
      setFaqCategory(faqCategories.filter((e) => e.Category.toLowerCase() === categoryName.split('-').join(' ')).pop());
    }
  }, [faqCategories, categoryName]);

  useEffect(() => {
    if (faqCategory) {
      setFaqSubCategories(faqCategory.SubCategories);
    }
  }, [faqCategory]);

  useEffect(() => {
    if (categories.Success) {
      Object.values(categories.FAQCategories).map((category) => {
        const convertedInfo = category.Description.split('|').map((item) => item.trim());
        setFaqCategoriesList((prevCategory) => [
          ...prevCategory,
          {
            title: category.Category,
            position: category.Position,
            info: convertedInfo,
            logo: category.IconBase64,
          },
        ]);
      });
    }
  }, [categories]);

  useEffect(() => {
    if (faqCategoriesList.length !== 0) {
      setDataFetchingInfo(false, 'Loading...', 'fullScreen');
      setDataLoaded(true);
    } else {
      const interval = setInterval(() => {
        setDataFetchingInfo(false, 'Loading...', 'fullScreen');
        setDataFetchingInfo(true, 'Loading...', 'fullScreen');
      }, 150);
      return () => {
        clearInterval(interval);
      };
    }
    return () => {};
  }, [faqCategoriesList]);

  return (
    <div className={styles.category__container}>
      <div className={styles.category__leftContainer}>
        <FAQNavLeft FAQCategories={faqCategoriesList} />
      </div>
      <div className={styles.category__rightContainer}>
        <div className={styles.searchContainer}>
          <FAQSearchBar position="flex-end" borderColor="none" searchedValue="" />
        </div>
        <SubTitle
          subTitle={categoryName
            .replace(/vopay/g, 'VoPay')
            .replace(/api/g, 'API')
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}
          fontSize="20px"
        />
        {faqCategories && (
          <>
            {Object.values(faqSubCategories).map((e) => (
              <FAQCategoriesDropdownMenu
                menuName={e.SubCategory}
                questions={e.Questions}
                categoryName={categoryName}
                dataLoaded={dataLoaded}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default FAQCategories;
